import { assertFocusIdentity, focusIdentitySession, type FocusIdentityGuard } from "./ownership";
import { executeReviewedFocusAction, acknowledgeFocusAction, newFocusOperationId, type FocusSnapshot } from "@/lib/focusExecutor";
import { canonicalFocusSnapshot } from "../../../supabase/functions/_shared/focusActionDomain";
import { publishFocusRefresh } from "@/lib/focusChanges";
// Focus Tier 3 "Adaptive Mirror" helpers.
//
// Two pieces:
//   1. setFocusQuality(sessionId, quality) — single UPDATE on the
//      just-completed focus_sessions row.
//   2. loadAdaptiveContext + pickSuggestion + cacheSuggestion — read the
//      previous session quality + the most recent sleep quality, compute
//      a calm next-session suggestion, and persist it to
//      focus_react_cache (Pattern B). The Mirror chat consumes that
//      cache; the Focus page renders the same text inline.
//
// All writes are best-effort: silent failure must not block the user
// from starting a new session.

import { supabase } from "@/integrations/supabase/client";

const LOG = "[focus/tier3]";

export type FocusQuality = "flow" | "bom" | "medio" | "distraido";
export type SleepQuality = "amazing" | "good" | "fair" | "poor" | "terrible";

export const FOCUS_QUALITY_OPTIONS: { value: FocusQuality; emoji: string; labelKey: string }[] = [
  { value: "flow",      emoji: "\u{1F30A}", labelKey: "focus.tier3.quality.flow" },
  { value: "bom",       emoji: "✓",     labelKey: "focus.tier3.quality.bom" },
  { value: "medio",     emoji: "~",          labelKey: "focus.tier3.quality.medio" },
  { value: "distraido", emoji: "✗",     labelKey: "focus.tier3.quality.distraido" },
];

export async function setFocusQuality(sessionId: string, quality: FocusQuality, snapshot?: FocusSnapshot, ownerGuard?: FocusIdentityGuard): Promise<boolean> {
  if (!snapshot) return false;
  try {
    assertFocusIdentity(ownerGuard);
    const original = canonicalFocusSnapshot(structuredClone(snapshot));
    if (original.session.id !== sessionId || ownerGuard && ownerGuard.ownerId !== original.session.user_id) return false;
    const request = { action: "update" as const, focus_session_id: sessionId, operation_id: newFocusOperationId(), confirmed: true as const, expected_snapshot: original, focus_quality: quality };
    const assert = () => assertFocusIdentity(ownerGuard);
    const done = await executeReviewedFocusAction(original.session.user_id, request, assert); assert();
    await acknowledgeFocusAction(done.entry, assert); assert(); publishFocusRefresh(original.session.user_id); return true;
  } catch { return false; }
}

export interface AdaptiveContext {
  // The most recent prior focus_sessions row (with focus_quality set OR
  // null). Null when no prior session exists.
  lastSessionId: string | null;
  lastQuality: FocusQuality | null;
  lastDurationMinutes: number | null;
  // Latest sleep_logs.quality entry within the last 36h, or null when no
  // recent sleep log is available. We intentionally cap the window so a
  // stale weekend log does not skew weekday suggestions.
  lastSleepQuality: SleepQuality | null;
}

export async function loadAdaptiveContext(expectedUserId?: string, ownerGuard?: FocusIdentityGuard): Promise<AdaptiveContext> {
  const empty: AdaptiveContext = {
    lastSessionId: null,
    lastQuality: null,
    lastDurationMinutes: null,
    lastSleepQuality: null,
  };
  try {
    assertFocusIdentity(ownerGuard);
    const pinned = ownerGuard ? await focusIdentitySession(ownerGuard) : null;
    const { data: { user } } = await supabase.auth.getUser();
    assertFocusIdentity(ownerGuard);
    if (!user || (expectedUserId && user.id !== expectedUserId)) return empty;

    const cutoff = new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString();

    const owned = <T extends { setHeader(name: string, value: string): T }>(query: T): T => pinned ? query.setHeader("Authorization", "Bearer " + pinned.access_token) : query;
    const [focusRes, sleepRes] = await Promise.all([
      owned((supabase as any)
        .from("focus_sessions")
        .select("id, focus_quality, duration_minutes, completed_at")
        .eq("user_id", user.id)
        .order("completed_at", { ascending: false })
        .limit(1)
        .maybeSingle()),
      owned((supabase as any)
        .from("sleep_logs")
        .select("quality, sleep_date, created_at")
        .eq("user_id", user.id)
        .gte("created_at", cutoff)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle()),
    ]);
    assertFocusIdentity(ownerGuard);
    if (ownerGuard) await focusIdentitySession(ownerGuard);
    assertFocusIdentity(ownerGuard);

    if (focusRes.error) {
      console.error(LOG + " focus_sessions read failed:", focusRes.error);
    }
    if (sleepRes.error) {
      console.error(LOG + " sleep_logs read failed (ignored):", sleepRes.error);
    }

    return {
      lastSessionId: focusRes.data?.id ?? null,
      lastQuality: (focusRes.data?.focus_quality ?? null) as FocusQuality | null,
      lastDurationMinutes: focusRes.data?.duration_minutes ?? null,
      lastSleepQuality: (sleepRes.data?.quality ?? null) as SleepQuality | null,
    };
  } catch (e) {
    console.error(LOG + " loadAdaptiveContext exception:", e);
    return empty;
  }
}

export interface Suggestion {
  // i18n key for the body text. Empty string means "no suggestion".
  textKey: string;
  // Concrete minutes to spotlight in the suggestion (e.g. "comece com 25").
  // Caller still lets the user pick any duration; this is informational.
  suggestedMinutes: number;
  // True when sleep quality nudged the wording (logged in PR description
  // so we can sanity-check the surface in QA).
  sleepAdjusted: boolean;
}

// Pure: no IO, easy to reason about. The "shorter" path on poor sleep is
// what protects users who completed a 'flow' session but slept badly —
// recovery beats heroics.
export function pickSuggestion(ctx: AdaptiveContext): Suggestion {
  const { lastQuality, lastDurationMinutes, lastSleepQuality } = ctx;
  const sleepLow = lastSleepQuality === "poor" || lastSleepQuality === "terrible";

  if (!lastQuality) {
    return { textKey: "", suggestedMinutes: 25, sleepAdjusted: false };
  }

  if (lastQuality === "distraido" || lastQuality === "medio") {
    return {
      textKey: sleepLow
        ? "focus.tier3.suggest.shorter_sleepLow"
        : "focus.tier3.suggest.shorter",
      suggestedMinutes: 25,
      sleepAdjusted: sleepLow,
    };
  }

  // flow / bom — keep cadence or step up one tier (25 → 45). Never push
  // past 45 automatically; the 90-min Flow State stays an opt-in.
  const next = lastDurationMinutes && lastDurationMinutes >= 45
    ? lastDurationMinutes
    : 45;
  return {
    textKey: sleepLow
      ? "focus.tier3.suggest.keep_sleepLow"
      : (lastDurationMinutes === 25 ? "focus.tier3.suggest.extend" : "focus.tier3.suggest.keep"),
    suggestedMinutes: next,
    sleepAdjusted: sleepLow,
  };
}

export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}|\{(\w+)\}/g, (_, doubled, single) => { const key = doubled ?? single; return values[key] != null ? String(values[key]) : `{${key}}`; });
}

// Best-effort cache write (Pattern B). One row per (focus_session_id,
// language) — the unique index makes duplicate writes a no-op. Safe to
// call on every adaptive context refresh.
export async function cacheFocusReaction(params: {
  userId: string;
  focusSessionId: string;
  message: string;
  language: string;
}, ownerGuard?: FocusIdentityGuard): Promise<void> {
  try {
    assertFocusIdentity(ownerGuard);
    const pinned = ownerGuard ? await focusIdentitySession(ownerGuard) : null;
    const { data: { user } } = await supabase.auth.getUser();
    assertFocusIdentity(ownerGuard);
    if (!user || user.id !== params.userId || ownerGuard && ownerGuard.ownerId !== params.userId) return;
    const pin = <T extends { setHeader(name: string, value: string): T }>(query: T): T => pinned ? query.setHeader("Authorization", "Bearer " + pinned.access_token) : query;
    const owned = await pin((supabase as any).from("focus_sessions").select("id").eq("id", params.focusSessionId).eq("user_id", user.id).maybeSingle());
    assertFocusIdentity(ownerGuard);
    if (owned.error || !owned.data) return;
    const cached = await pin((supabase as any).from("focus_react_cache").select("id,message").eq("user_id", user.id).eq("focus_session_id", params.focusSessionId).eq("language", params.language).maybeSingle());
    assertFocusIdentity(ownerGuard);
    if (cached.error || cached.data?.message === params.message) return;
    const { data: { user: latest } } = await supabase.auth.getUser();
    assertFocusIdentity(ownerGuard);
    if (latest?.id !== params.userId) return;
    // This table permits DELETE/INSERT, but has no UPDATE policy. Replace only
    // the version read above, so a concurrent refresh cannot remove newer text.
    if (cached.data) {
      assertFocusIdentity(ownerGuard);
      const removed = await pin((supabase as any).from("focus_react_cache").delete().eq("id", cached.data.id).eq("user_id", user.id).eq("message", cached.data.message));
      assertFocusIdentity(ownerGuard);
      if (removed.error) return;
      const { data: { user: current } } = await supabase.auth.getUser();
      assertFocusIdentity(ownerGuard);
      if (current?.id !== params.userId) return;
    }
    assertFocusIdentity(ownerGuard);
    const { error } = await pin((supabase as any)
      .from("focus_react_cache")
      .upsert(
        {
          user_id: user.id,
          focus_session_id: params.focusSessionId,
          message: params.message,
          language: params.language,
          created_at: new Date().toISOString(),
        },
        { onConflict: "focus_session_id,language", ignoreDuplicates: true },
      ));
    assertFocusIdentity(ownerGuard);
    if (ownerGuard) await focusIdentitySession(ownerGuard);
    assertFocusIdentity(ownerGuard);
    if (error) {
      console.error(LOG + " focus_react_cache upsert failed (ignored):", error);
    }
  } catch (e) {
    console.error(LOG + " cacheFocusReaction exception:", e);
  }
}
